import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Card, CardContent, Stepper, Step, StepLabel, Button, Stack, TextField, MenuItem, Chip, Alert, Box, Typography,
  IconButton, FormControlLabel, Checkbox, Snackbar, Divider, CircularProgress,
} from '@mui/material';
import { Add, Delete, Save, Send } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { PageHeader } from '../../../components/PageHeader';
import { vendorOnboardingApi, VENDOR_TYPE_GROUPS, ALL_VENDOR_TYPES, type VendorApplication } from '../../../api/vendorOnboarding.api';
import { extractApiError } from '../../../api/error';

const STEPS = ['Classification', 'Company', 'Address', 'Contacts', 'Capability', 'Experience', 'Tools', 'Financial', 'Commercial', 'Bank', 'Documents', 'Declaration'];

// Dynamic type-specific fields (Part 3 dynamic logic) keyed by primary vendor type.
const TYPE_FIELDS: Record<string, Array<{ key: string; label: string; type?: string }>> = {
  'Electrical Contractor': [
    { key: 'electricalLicenseNumber', label: 'Electrical License Number' },
    { key: 'licensedElectricians', label: 'No. of Licensed Electricians', type: 'number' },
    { key: 'supervisors', label: 'No. of Supervisors', type: 'number' },
    { key: 'testingEngineers', label: 'No. of Testing Engineers', type: 'number' },
  ],
  'Civil Contractor': [
    { key: 'civilTeamStrength', label: 'Civil Team Strength', type: 'number' },
    { key: 'foundationExperience', label: 'Foundation Experience' },
    { key: 'equipmentAvailability', label: 'Equipment Availability' },
  ],
  'Fabrication Vendor': [
    { key: 'workshopDetails', label: 'Workshop Details' },
    { key: 'monthlyProductionCapacity', label: 'Monthly Production Capacity', type: 'number' },
  ],
  'OEM / Manufacturer': [
    { key: 'factoryArea', label: 'Factory Area (sq.ft)', type: 'number' },
    { key: 'dailyProductionCapacity', label: 'Daily Production Capacity', type: 'number' },
    { key: 'productPortfolio', label: 'Product Portfolio' },
  ],
  'SLA / Maintenance Partner': [
    { key: 'responseTAT', label: 'Response TAT (hours)', type: 'number' },
    { key: 'ticketHandlingCapacity', label: 'Ticket Handling Capacity', type: 'number' },
    { key: 'support247', label: '24x7 Support (yes/no)' },
  ],
  'Logistics Partner': [
    { key: 'vehicleCount', label: 'Vehicle Count', type: 'number' },
    { key: 'coverageArea', label: 'Coverage Area' },
    { key: 'gpsTracking', label: 'GPS Tracking (yes/no)' },
  ],
};

type Row = Record<string, unknown>;

function Repeater({ title, rows, setRows, fields, addLabel, readOnly }: { title: string; rows: Row[]; setRows: (r: Row[]) => void; fields: Array<{ key: string; label: string; type?: string }>; addLabel: string; readOnly: boolean }) {
  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>{title}</Typography>
      <Stack spacing={1.5}>
        {rows.map((row, i) => (
          <Stack key={i} direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            {fields.map((f) => (
              <TextField key={f.key} size="small" label={f.label} type={f.type ?? 'text'} value={(row[f.key] as string) ?? ''} disabled={readOnly}
                onChange={(e) => setRows(rows.map((r, idx) => (idx === i ? { ...r, [f.key]: f.type === 'number' ? Number(e.target.value) : e.target.value } : r)))} sx={{ minWidth: 150 }} />
            ))}
            {!readOnly && <IconButton size="small" color="error" onClick={() => setRows(rows.filter((_, idx) => idx !== i))}><Delete fontSize="small" /></IconButton>}
          </Stack>
        ))}
      </Stack>
      {!readOnly && <Button size="small" startIcon={<Add />} sx={{ mt: 1 }} onClick={() => setRows([...rows, {}])}>{addLabel}</Button>}
    </Box>
  );
}

const STATUS_LABEL: Record<string, string> = {
  draft: 'Draft', submitted: 'Submitted', document_verification_pending: 'Document Verification Pending', clarification_required: 'Clarification Required',
  technical_evaluation_pending: 'Technical Evaluation Pending', commercial_evaluation_pending: 'Commercial Evaluation Pending',
  final_approval_pending: 'Final Approval Pending', approved: 'Approved', conditional_approval: 'Conditional Approval', rejected: 'Rejected', active: 'Active',
  sent_back: 'Sent Back for Correction', withdrawn: 'Withdrawn',
};

export function VendorOnboardingWizard() {
  const qc = useQueryClient();
  // Admin / PM / ROM edit a specific application via /vendor-applications/:id/edit;
  // a vendor self-services their own via /vendor-portal/onboarding (no id → /me).
  const params = useParams<{ id?: string }>();
  const routeId = params.id ? Number(params.id) : null;
  const queryKey = routeId ? ['vendor-application-edit', routeId] : ['vendor-onboarding-me'];
  const { data, isLoading } = useQuery({ queryKey, queryFn: () => (routeId ? vendorOnboardingApi.get(routeId) : vendorOnboardingApi.me()) });
  const [form, setForm] = useState<Partial<VendorApplication>>({});
  const [activeStep, setActiveStep] = useState(0);
  const [toast, setToast] = useState<string | null>(null);
  const formRef = useRef(form);
  formRef.current = form;

  useEffect(() => { if (data) setForm(data); }, [data]);

  const editable = form.status === 'draft' || form.status === 'clarification_required' || form.status === 'sent_back';
  const id = form.id;
  const invalidate = () => qc.invalidateQueries({ queryKey });

  const set = (k: keyof VendorApplication, v: unknown) => setForm((f) => ({ ...f, [k]: v }));
  const setChild = (k: keyof VendorApplication, rows: Row[]) => setForm((f) => ({ ...f, [k]: rows }));
  const tsd = (form.type_specific_data as Record<string, unknown>) ?? {};
  const setTsd = (key: string, v: unknown) => set('type_specific_data', { ...tsd, [key]: v });

  function payload(autosave: boolean): Record<string, unknown> {
    const f = formRef.current;
    return {
      __autosave: autosave,
      primary_vendor_type: f.primary_vendor_type, legal_name: f.legal_name, trade_name: f.trade_name, entity_type: f.entity_type,
      gst_number: f.gst_number, pan_number: f.pan_number, cin_number: f.cin_number, udyam_number: f.udyam_number, website: f.website,
      year_established: f.year_established, employee_count: f.employee_count, payment_terms: f.payment_terms, credit_period_days: f.credit_period_days,
      msme_registered: f.msme_registered, gst_registered: f.gst_registered, iso_certified: f.iso_certified, has_safety_policy: f.has_safety_policy,
      has_quality_policy: f.has_quality_policy, declaration_accepted: f.declaration_accepted, declaration_name: f.declaration_name,
      type_specific_data: f.type_specific_data ?? {}, completed_steps: f.completed_steps ?? [],
      secondaryCategories: f.secondaryCategories, contacts: f.contacts, addresses: f.addresses, serviceCapabilities: f.serviceCapabilities,
      oemExperience: f.oemExperience, projectExperience: f.projectExperience, toolsEquipment: f.toolsEquipment, financials: f.financials,
      bankDetails: f.bankDetails, sla: f.sla,
    };
  }

  const saveMut = useMutation({
    mutationFn: (autosave: boolean) => vendorOnboardingApi.saveDraft(id!, payload(autosave)),
    onSuccess: (res, autosave) => { setForm(res); if (!autosave) setToast('Draft saved'); },
    onError: (e) => setToast(extractApiError(e)),
  });
  const submitMut = useMutation({
    // Persist the current form (declaration, contacts, last-step edits) BEFORE submitting so the
    // server validates the latest data — otherwise unsaved changes cause a spurious 422.
    mutationFn: async () => { await vendorOnboardingApi.saveDraft(id!, payload(true)); return vendorOnboardingApi.submit(id!); },
    onSuccess: (res) => { setForm(res); invalidate(); setToast('Application submitted!'); },
    onError: (e) => setToast(extractApiError(e)),
  });
  const respondMut = useMutation({
    mutationFn: async () => { await vendorOnboardingApi.saveDraft(id!, payload(true)); return vendorOnboardingApi.respondClarification(id!); },
    onSuccess: (res) => { setForm(res); setToast('Re-submitted for review'); },
    onError: (e) => setToast(extractApiError(e)),
  });

  // Autosave every 2 minutes while editable.
  useEffect(() => {
    if (!editable || !id) return undefined;
    const t = setInterval(() => saveMut.mutate(true), 120_000);
    return () => clearInterval(t);
  }, [editable, id]); // eslint-disable-line react-hooks/exhaustive-deps

  const secondary = useMemo(() => (form.secondaryCategories ?? []).map((c) => c.category as string), [form.secondaryCategories]);
  const dynFields = TYPE_FIELDS[form.primary_vendor_type ?? ''] ?? [];

  if (isLoading || !id) return <Box p={4} textAlign="center"><CircularProgress /></Box>;

  return (
    <>
      <PageHeader title="Vendor Onboarding" subtitle={`Application ${form.application_code}`}
        action={<Chip color={form.status === 'approved' || form.status === 'active' ? 'success' : form.status === 'rejected' ? 'error' : 'info'} label={STATUS_LABEL[form.status ?? 'draft'] ?? form.status} />} />

      {!editable && form.status !== 'active' && form.status !== 'approved' && (
        <Alert severity="info" sx={{ mb: 2 }}>Your application is <b>{STATUS_LABEL[form.status ?? ''] ?? form.status}</b>. You can track progress here; editing is locked while under review.</Alert>
      )}
      {(form.status === 'approved' || form.status === 'active') && (
        <Alert severity="success" sx={{ mb: 2 }}>Your application is approved and your vendor account is active. Welcome aboard!</Alert>
      )}
      {form.status === 'clarification_required' && (
        <Alert severity="warning" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={() => respondMut.mutate()}>Re-submit</Button>}>
          Clarification was requested. Update the details below and re-submit.
        </Alert>
      )}
      {form.status === 'sent_back' && (
        <Alert severity="warning" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={() => submitMut.mutate()}>Re-submit</Button>}>
          This application was sent back for correction. Update the details below and re-submit.
        </Alert>
      )}

      <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3, flexWrap: 'wrap' }}>
        {STEPS.map((s) => <Step key={s}><StepLabel>{s}</StepLabel></Step>)}
      </Stepper>

      <Card>
        <CardContent>
          <Stack spacing={2}>
            {activeStep === 0 && (
              <>
                <TextField select label="Primary Vendor Type *" value={form.primary_vendor_type ?? ''} disabled={!editable} onChange={(e) => set('primary_vendor_type', e.target.value)}>
                  {Object.entries(VENDOR_TYPE_GROUPS).flatMap(([group, types]) => [
                    <MenuItem key={group} disabled sx={{ fontWeight: 700, opacity: 0.8 }}>{group}</MenuItem>,
                    ...types.map((t) => <MenuItem key={t} value={t} sx={{ pl: 4 }}>{t}</MenuItem>),
                  ])}
                </TextField>
                <TextField select SelectProps={{ multiple: true, renderValue: (v) => (v as string[]).join(', ') }} label="Secondary Categories" value={secondary} disabled={!editable}
                  onChange={(e) => setChild('secondaryCategories', (e.target.value as unknown as string[]).map((category) => ({ category })))}>
                  {ALL_VENDOR_TYPES.map((t) => <MenuItem key={t} value={t}><Checkbox checked={secondary.includes(t)} size="small" />{t}</MenuItem>)}
                </TextField>
              </>
            )}
            {activeStep === 1 && (
              <Stack direction="row" flexWrap="wrap" gap={2}>
                {([['legal_name', 'Company Name *'], ['trade_name', 'Trade Name'], ['entity_type', 'Business Type'], ['gst_number', 'GST Number'], ['pan_number', 'PAN Number'], ['cin_number', 'CIN'], ['udyam_number', 'MSME / Udyam No.'], ['website', 'Website']] as const).map(([k, label]) =>
                  <TextField key={k} label={label} value={(form[k] as string) ?? ''} disabled={!editable} onChange={(e) => set(k, e.target.value)} sx={{ minWidth: 220 }} />)}
                <TextField label="Year of Establishment" type="number" value={form.year_established ?? ''} disabled={!editable} onChange={(e) => set('year_established', Number(e.target.value))} sx={{ minWidth: 180 }} />
                <TextField label="Employee Count" type="number" value={form.employee_count ?? ''} disabled={!editable} onChange={(e) => set('employee_count', Number(e.target.value))} sx={{ minWidth: 180 }} />
              </Stack>
            )}
            {activeStep === 2 && <Repeater title="Office Addresses" rows={form.addresses ?? []} setRows={(r) => setChild('addresses', r)} readOnly={!editable} addLabel="Add address"
              fields={[{ key: 'address_type', label: 'Type' }, { key: 'line1', label: 'Address' }, { key: 'city', label: 'City' }, { key: 'pincode', label: 'Pincode' }]} />}
            {activeStep === 3 && <Repeater title="Contacts" rows={form.contacts ?? []} setRows={(r) => setChild('contacts', r)} readOnly={!editable} addLabel="Add contact"
              fields={[{ key: 'contact_type', label: 'Type' }, { key: 'name', label: 'Name' }, { key: 'designation', label: 'Designation' }, { key: 'email', label: 'Email' }, { key: 'mobile', label: 'Mobile' }]} />}
            {activeStep === 4 && (
              <>
                <Repeater title="Service Capabilities" rows={form.serviceCapabilities ?? []} setRows={(r) => setChild('serviceCapabilities', r)} readOnly={!editable} addLabel="Add capability"
                  fields={[{ key: 'capability', label: 'Capability' }, { key: 'category', label: 'Category' }]} />
                {dynFields.length > 0 && <>
                  <Divider />
                  <Typography variant="subtitle2">{form.primary_vendor_type} — specific details</Typography>
                  <Stack direction="row" flexWrap="wrap" gap={2}>
                    {dynFields.map((f) => <TextField key={f.key} label={f.label} type={f.type ?? 'text'} value={(tsd[f.key] as string) ?? ''} disabled={!editable} onChange={(e) => setTsd(f.key, f.type === 'number' ? Number(e.target.value) : e.target.value)} sx={{ minWidth: 220 }} />)}
                  </Stack>
                </>}
              </>
            )}
            {activeStep === 5 && (
              <>
                <Repeater title="OEM / CPO Experience" rows={form.oemExperience ?? []} setRows={(r) => setChild('oemExperience', r)} readOnly={!editable} addLabel="Add OEM/CPO experience"
                  fields={[{ key: 'oem_name', label: 'OEM/CPO Name' }, { key: 'product_category', label: 'Scope' }, { key: 'projects_count', label: 'Sites', type: 'number' }, { key: 'years', label: 'Year', type: 'number' }]} />
                <Divider />
                <Repeater title="Project Experience" rows={form.projectExperience ?? []} setRows={(r) => setChild('projectExperience', r)} readOnly={!editable} addLabel="Add project"
                  fields={[{ key: 'project_name', label: 'Project' }, { key: 'client_name', label: 'Client' }, { key: 'location', label: 'Location' }, { key: 'value', label: 'Value', type: 'number' }]} />
              </>
            )}
            {activeStep === 6 && <Repeater title="Tools & Equipment" rows={form.toolsEquipment ?? []} setRows={(r) => setChild('toolsEquipment', r)} readOnly={!editable} addLabel="Add equipment"
              fields={[{ key: 'equipment_name', label: 'Equipment' }, { key: 'quantity', label: 'Qty', type: 'number' }, { key: 'ownership', label: 'Owned/Rented' }]} />}
            {activeStep === 7 && (
              <>
                <Repeater title="Financials" rows={form.financials ?? []} setRows={(r) => setChild('financials', r)} readOnly={!editable} addLabel="Add financial year"
                  fields={[{ key: 'financial_year', label: 'FY' }, { key: 'annual_turnover', label: 'Turnover', type: 'number' }, { key: 'net_worth', label: 'Net Worth', type: 'number' }]} />
                <Stack direction="row" flexWrap="wrap" gap={2}>
                  {([['gst_registered', 'GST Registered'], ['msme_registered', 'MSME Registered'], ['iso_certified', 'ISO 9001'], ['has_safety_policy', 'ISO 45001 / Safety'], ['has_quality_policy', 'Quality Policy']] as const).map(([k, label]) =>
                    <FormControlLabel key={k} control={<Checkbox checked={Boolean(form[k])} disabled={!editable} onChange={(e) => set(k, e.target.checked)} />} label={label} />)}
                </Stack>
              </>
            )}
            {activeStep === 8 && (
              <Stack direction="row" flexWrap="wrap" gap={2}>
                <TextField label="Preferred Payment Terms" value={form.payment_terms ?? ''} disabled={!editable} onChange={(e) => set('payment_terms', e.target.value)} sx={{ minWidth: 240 }} />
                <TextField label="Credit Period (days)" type="number" value={form.credit_period_days ?? ''} disabled={!editable} onChange={(e) => set('credit_period_days', Number(e.target.value))} sx={{ minWidth: 200 }} />
              </Stack>
            )}
            {activeStep === 9 && <Repeater title="Bank Details" rows={form.bankDetails ?? []} setRows={(r) => setChild('bankDetails', r)} readOnly={!editable} addLabel="Add bank account"
              fields={[{ key: 'bank_name', label: 'Bank' }, { key: 'account_name', label: 'Account Holder' }, { key: 'account_number', label: 'Account No.' }, { key: 'ifsc', label: 'IFSC' }, { key: 'branch', label: 'Branch' }]} />}
            {activeStep === 10 && <Alert severity="info">Mandatory documents (GST Certificate, PAN, Company Profile, Cancelled Cheque) and optional compliance documents are uploaded to the Document Repository. Use the Documents action to attach files.</Alert>}
            {activeStep === 11 && (
              <Stack spacing={2}>
                <TextField label="Authorized Person Name *" value={form.declaration_name ?? ''} disabled={!editable} onChange={(e) => set('declaration_name', e.target.value)} sx={{ maxWidth: 360 }} />
                <FormControlLabel control={<Checkbox checked={Boolean(form.declaration_accepted)} disabled={!editable} onChange={(e) => set('declaration_accepted', e.target.checked)} />}
                  label="I hereby declare that all information submitted is true and correct." />
              </Stack>
            )}
          </Stack>

          <Stack direction="row" justifyContent="space-between" mt={3}>
            <Button disabled={activeStep === 0} onClick={() => setActiveStep((s) => s - 1)}>Previous</Button>
            <Stack direction="row" spacing={1}>
              {editable && <Button startIcon={<Save />} variant="outlined" disabled={saveMut.isPending} onClick={() => saveMut.mutate(false)}>Save Draft</Button>}
              {activeStep < STEPS.length - 1 && <Button variant="contained" onClick={() => { if (editable) saveMut.mutate(true); setActiveStep((s) => s + 1); }}>Next</Button>}
              {activeStep === STEPS.length - 1 && editable && <Button startIcon={<Send />} variant="contained" color="success" disabled={submitMut.isPending} onClick={() => submitMut.mutate()}>Final Submit</Button>}
            </Stack>
          </Stack>
        </CardContent>
      </Card>
      <Snackbar open={Boolean(toast)} autoHideDuration={4000} onClose={() => setToast(null)}>
        <Alert severity="info" onClose={() => setToast(null)}>{toast}</Alert>
      </Snackbar>
    </>
  );
}
